import { useEffect, useState } from "react";

/**
 * Dismissible banner.
 *
 * State persistence: each banner has a `dismissKey` which gets stored in
 * localStorage when dismissed. The banner stays dismissed across reloads
 * until the user explicitly clears localStorage or the key changes.
 *
 * Use for "API not configured" warnings, deprecation notices, etc.
 *
 * NOTE: Per the artifact storage rule, this is the chat app, not an
 * artifact, so localStorage is fine here. Artifacts use window.storage.
 */
export interface DismissibleBannerProps {
  dismissKey: string;
  kind?: "warning" | "danger" | "info";
  title?: string;
  children: React.ReactNode;
  /** If true, banner is shown by default unless user dismissed it. */
  initialOpen?: boolean;
}

export function DismissibleBanner({
  dismissKey,
  kind = "danger",
  title,
  children,
  initialOpen = true,
}: DismissibleBannerProps) {
  const storageKey = `smc-banner-dismissed:${dismissKey}`;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(storageKey);
      setOpen(initialOpen && dismissed !== "1");
    } catch {
      setOpen(initialOpen);
    }
  }, [storageKey, initialOpen]);

  function handleDismiss() {
    try {
      localStorage.setItem(storageKey, "1");
    } catch {
      // ignore
    }
    setOpen(false);
  }

  if (!open) return null;

  const styles = STYLES[kind];

  return (
    <div
      className={`flex items-start gap-2 ${styles.bg} ${styles.border} border rounded-md px-4 py-2.5`}
    >
      <span className={`${styles.iconColor} text-base`}>{styles.icon}</span>
      <div className="flex-1 text-sm">
        {title && <span className={`${styles.titleColor} font-semibold mr-2`}>{title}</span>}
        <span className={styles.bodyColor}>{children}</span>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        className={`${styles.bodyColor} hover:opacity-80 leading-none text-base`}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}

const STYLES = {
  warning: {
    bg: "bg-warning-bg",
    border: "border-warning-border",
    icon: "⚠",
    iconColor: "text-warning",
    titleColor: "text-warning",
    bodyColor: "text-text",
  },
  danger: {
    bg: "bg-danger-bg",
    border: "border-danger-border",
    icon: "⚠",
    iconColor: "text-danger",
    titleColor: "text-danger",
    bodyColor: "text-text",
  },
  info: {
    bg: "bg-accent-subtle",
    border: "border-accent/30",
    icon: "ℹ",
    iconColor: "text-accent",
    titleColor: "text-accent",
    bodyColor: "text-text",
  },
};
